
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const InterestCalculator: React.FC = () => {
  // Simple Interest
  const [simpleInterestPrincipal, setSimpleInterestPrincipal] = useState<number>(10000);
  const [simpleInterestRate, setSimpleInterestRate] = useState<number>(5);
  const [simpleInterestTime, setSimpleInterestTime] = useState<number>(3);
  const [simpleInterestTimeUnit, setSimpleInterestTimeUnit] = useState<string>("years");
  const [simpleInterestResult, setSimpleInterestResult] = useState<number>(0);
  const [simpleInterestTotal, setSimpleInterestTotal] = useState<number>(0);
  
  // Compound Interest
  const [compoundPrincipal, setCompoundPrincipal] = useState<number>(10000);
  const [compoundRate, setCompoundRate] = useState<number>(5);
  const [compoundTime, setCompoundTime] = useState<number>(5);
  const [compoundFrequency, setCompoundFrequency] = useState<string>("annually");
  const [compoundResult, setCompoundResult] = useState<number>(0);
  const [compoundTotal, setCompoundTotal] = useState<number>(0);
  
  // Calculator type
  const [calculatorType, setCalculatorType] = useState<string>("simple");

  const calculateSimpleInterest = () => {
    try {
      // Validation
      if (simpleInterestPrincipal < 0 || simpleInterestRate < 0 || simpleInterestTime <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }

      // Convert rate to decimal
      const rateDecimal = simpleInterestRate / 100;
      
      // Convert time to years if needed
      let timeInYears = simpleInterestTime;
      if (simpleInterestTimeUnit === "months") {
        timeInYears = simpleInterestTime / 12;
      } else if (simpleInterestTimeUnit === "days") {
        timeInYears = simpleInterestTime / 365;
      }
      
      // Calculate simple interest: P × r × t
      const interest = simpleInterestPrincipal * rateDecimal * timeInYears;
      
      // Calculate total amount: P + interest
      const total = simpleInterestPrincipal + interest;
      
      setSimpleInterestResult(interest);
      setSimpleInterestTotal(total);

      toast({
        title: "Calculation complete",
        description: "Simple interest has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const calculateCompoundInterest = () => {
    try {
      // Validation
      if (compoundPrincipal < 0 || compoundRate < 0 || compoundTime <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }
      
      // Convert rate to decimal
      const rateDecimal = compoundRate / 100;
      
      // Determine number of times interest is compounded per year
      let n = 1; // Default: annually
      if (compoundFrequency === "semi-annually") n = 2;
      else if (compoundFrequency === "quarterly") n = 4;
      else if (compoundFrequency === "monthly") n = 12;
      else if (compoundFrequency === "daily") n = 365;
      
      // Calculate compound amount: P(1 + r/n)^(nt)
      const compoundAmount = compoundPrincipal * Math.pow(1 + rateDecimal / n, n * compoundTime);
      
      // Calculate compound interest
      const compoundInterest = compoundAmount - compoundPrincipal;
      
      setCompoundResult(compoundInterest);
      setCompoundTotal(compoundAmount);

      toast({
        title: "Calculation complete",
        description: "Compound interest has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <Tabs value={calculatorType} onValueChange={setCalculatorType}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="simple">Simple Interest</TabsTrigger>
          <TabsTrigger value="compound">Compound Interest</TabsTrigger>
        </TabsList>
        
        {/* Simple Interest Calculator */}
        <TabsContent value="simple" className="space-y-6 pt-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="simpleInterestPrincipal">Principal Amount ($)</Label>
                <Input
                  id="simpleInterestPrincipal"
                  type="number"
                  value={simpleInterestPrincipal}
                  onChange={(e) => setSimpleInterestPrincipal(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="simpleInterestRate">Interest Rate (% per year)</Label>
                <Input
                  id="simpleInterestRate"
                  type="number"
                  value={simpleInterestRate}
                  onChange={(e) => setSimpleInterestRate(Number(e.target.value))}
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="simpleInterestTime">Time Period</Label>
                <div className="flex gap-2">
                  <Input
                    id="simpleInterestTime"
                    type="number"
                    value={simpleInterestTime}
                    onChange={(e) => setSimpleInterestTime(Number(e.target.value))}
                    className="flex-1"
                  />
                  <Select 
                    value={simpleInterestTimeUnit} 
                    onValueChange={setSimpleInterestTimeUnit}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="years">Years</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={calculateSimpleInterest} className="mt-4 w-full">Calculate Simple Interest</Button>
              
              <div className="text-sm text-muted-foreground">
                <p>Formula: Interest = Principal × Rate × Time</p>
              </div>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Simple Interest Results</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Principal Amount</div>
                      <div className="text-base font-medium">{formatCurrency(simpleInterestPrincipal)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Interest Earned</div>
                      <div className="text-lg font-medium text-primary">
                        {simpleInterestResult ? formatCurrency(simpleInterestResult) : '-'}
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Total Amount</div>
                      <div className="text-xl font-bold">
                        {simpleInterestTotal ? formatCurrency(simpleInterestTotal) : '-'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Compound Interest Calculator */}
        <TabsContent value="compound" className="space-y-6 pt-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="compoundPrincipal">Principal Amount ($)</Label>
                <Input
                  id="compoundPrincipal"
                  type="number"
                  value={compoundPrincipal}
                  onChange={(e) => setCompoundPrincipal(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="compoundRate">Annual Interest Rate (%)</Label>
                <Input
                  id="compoundRate"
                  type="number"
                  value={compoundRate}
                  onChange={(e) => setCompoundRate(Number(e.target.value))}
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="compoundTime">Time Period (years)</Label>
                <Input
                  id="compoundTime"
                  type="number"
                  value={compoundTime}
                  onChange={(e) => setCompoundTime(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="compoundFrequency">Compounding Frequency</Label>
                <Select 
                  value={compoundFrequency} 
                  onValueChange={setCompoundFrequency}
                >
                  <SelectTrigger id="compoundFrequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annually">Annually</SelectItem>
                    <SelectItem value="semi-annually">Semi-Annually</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={calculateCompoundInterest} className="mt-4 w-full">Calculate Compound Interest</Button>
              
              <div className="text-sm text-muted-foreground">
                <p>Formula: A = P(1 + r/n)^(nt)</p>
                <p className="mt-1">where:</p>
                <ul className="list-disc pl-5 mt-1 text-xs">
                  <li>A = Final amount</li>
                  <li>P = Principal</li>
                  <li>r = Annual rate (decimal)</li>
                  <li>n = Compounding frequency</li>
                  <li>t = Time (years)</li>
                </ul>
              </div>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Compound Interest Results</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Principal Amount</div>
                      <div className="text-base font-medium">{formatCurrency(compoundPrincipal)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Interest Earned</div>
                      <div className="text-lg font-medium text-primary">
                        {compoundResult ? formatCurrency(compoundResult) : '-'}
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Total Amount</div>
                      <div className="text-xl font-bold">
                        {compoundTotal ? formatCurrency(compoundTotal) : '-'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InterestCalculator;
